import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import {
	getChatGroup,
	getEncarPaySimpleLimitConditions,
	getEncarPayLoanSimpleLimitConditions,
	getHomeServiceApplications,
	postLoansEncarpayConsultations,
	postLoansEncarLoanConsultations,
	postCancelsHomeService,
	putPaymentMethods,
	postLoansEncarpayLimit,
	postLoansEncarloanLimit,
	postVerificationReportConfirm,
	postInspectionInsuranceConfirm,
	getHomeServiceUserProcess,
	getPaymentEstimates,
	getTransaction,
} from '@encarpkg/api';
import { ORDERS_PAYMENT_STATUS } from 'app/constants/orders';
import { HOMESERVICE_PAYMENT_METHODS } from 'app/constants/homeservice';
import { useUniversalCloser } from '@encarpkg/design';
import { goHome } from '@encarpkg/shared';
import { loggingException } from 'app/libs/sentry';

import useAuth from 'app/hook/common/useAuth';
import useGoLogin from 'app/hook/common/useGoLogin';
import useEstimate from 'app/hook/hs/useEstimate';

const buyHost = process.env.REACT_APP_ENCAR_FEM_BUY;
const TYPE_LIMIT = {
	READY: 'READY',
	POSSIBLE: 'POSSIBLE',
	IMPOSSIBLE: 'IMPOSSIBLE',
};

export default function useCustomerLead({ skipAuth, checkAccessEncarpayLimit } = { skipAuth: true, checkAccessEncarpayLimit: false }) {
	const location = useLocation();
	const { carId, customerLeadApplicationUuid, customerLeadApplicationId } = qs.parse(location.search);
	const [checkAuth, setCheckAuth] = useState(false);
	const [accessEncarpayLimitType, setAccessEncarpayLimitType] = useState(customerLeadApplicationUuid ? TYPE_LIMIT.READY : null);
	const [homeServiceInfo, setHomeServiceInfo] = useState(null);

	// 유저 정보
	const { initAuth, userInfo, isLoggedIn, getUser } = useAuth();

	// 차량 정보
	const { getPaymentEstimateInfo } = useEstimate();

	const goLogin = useGoLogin();

	const { handleUniversalClose } = useUniversalCloser({
		browser: { onNotClosed: goHome },
	});

	// subStepType값이 포함된 객체 값 찾기
	const findWorkflowSubStepStatus = useCallback((mainStepsData, subStepTypeStr) => {
		const subStep = mainStepsData.reduce((acc, step) => {
			if (Object.keys(acc).length !== 0) return acc;
			const found = step.subSteps.find((i) => i.subStepType === subStepTypeStr);
			return found || acc;
		}, {});

		return subStep;
	}, []);

	// encarpay - 차량 견적 정보
	const getPaymentEstimateInfoFn = useCallback(async () => {
		await getPaymentEstimateInfo(customerLeadApplicationUuid);
	}, [customerLeadApplicationUuid, getPaymentEstimateInfo]);

	const getApplication = useCallback(async () => {
		const { data } = await getHomeServiceApplications(customerLeadApplicationUuid, userInfo?.id, carId);
		return data;
	}, [carId, customerLeadApplicationUuid, userInfo?.id]);

	// encarpay, encarpay_loan - 재신청 체크, 재신청인 경우 엔카페이 hash 조회
	const getEncarPayHashFn = useCallback(
		async (paymentMethod, loanVersion) => {
			try {
				let hash = null;
				let reapplicable = false;
				if (paymentMethod === HOMESERVICE_PAYMENT_METHODS.ENCAR_LOAN) {
					const { data } = await getEncarPayLoanSimpleLimitConditions({ userId: userInfo?.id });
					hash = data.encarLoanHash;
					reapplicable = data.encarLoanLimitReapplicable;
				} else {
					const { data } = await getEncarPaySimpleLimitConditions({
						userId: userInfo?.id,
						...(loanVersion && { version: loanVersion }),
					});
					hash = data.encarPayHash;
					reapplicable = data.encarPayLimitReapplicable;
				}
				if (reapplicable) return hash;
				return null;
			} catch (e) {
				loggingException(e);
				return null;
			}
		},
		[userInfo?.id],
	);

	// encarpay - 기존조회, 명의자 정보 조회
	const getNomineesInfoFn = useCallback(async () => {
		try {
			const {
				data: { nominees },
			} = await getHomeServiceApplications(customerLeadApplicationUuid, userInfo?.id, carId);
			return {
				name: nominees[0]?.name,
				residentNumber1: nominees[0]?.birth.substr(2),
				phone: nominees[0]?.phone,
			};
		} catch (e) {
			console.error(e);
			loggingException(e);
			return null;
		}
	}, [carId, customerLeadApplicationUuid, userInfo?.id]);

	// encarpay - 홈서비스 엔카페이 금융 상담 신청
	const postEncarPayFinancialConsultationFn = useCallback(async () => {
		let consultationResult = false;
		let loggingError = null;

		try {
			await postLoansEncarpayConsultations(customerLeadApplicationUuid, {
				modifierType: 'USER',
			});
			consultationResult = true;
		} catch (e) {
			// 이미 상담 신청한 경우는 에러 케이스 X
			if (e?.response?.data?.code === 'ALREADY_EXIST_HOME_SERVICE_LOAN') {
				consultationResult = true;
			} else {
				loggingError = e;
			}
		}

		if (consultationResult) {
			window.location.href = `${buyHost}/home-service/financial-consultant?customerLeadApplicationUuid=${customerLeadApplicationUuid}`; // 금융상담 대기 안내 페이지
		} else {
			alert('일시적인 오류가 있습니다. 잠시후 다시 시도해주세요.');
			console.error(loggingError);
			loggingException(loggingError || '엔카페이 금융 상담 신청 실패');
		}
	}, [customerLeadApplicationUuid]);

	// encarloan - 홈서비스 대출 금융 상담 신청
	const postEncarLoanFinancialConsultationFn = useCallback(
		async (financialCompany = null, callback) => {
			let consultationResult = false;
			let loggingError = null;
			let loanApplicationId = null;

			try {
				const { data } = await postLoansEncarLoanConsultations(customerLeadApplicationUuid, {
					modifierType: 'USER',
					requestFinancialCompanyType: financialCompany,
				});
				loanApplicationId = data?.loanApplicationId;
				consultationResult = true;
			} catch (e) {
				// 이미 상담 신청한 경우는 에러 케이스 X
				if (e?.response?.data?.code === 'ALREADY_EXIST_HOME_SERVICE_LOAN') {
					consultationResult = true;
				} else {
					loggingError = e;
				}
			}
			callback(consultationResult, loggingError, loanApplicationId);
		},
		[customerLeadApplicationUuid],
	);

	// encarpay - 부결 [신청 취소하기]
	const postCancelsHomeServiceFn = useCallback(async () => {
		const params = {
			userValidation: {
				requesterType: 'USER',
				requesterId: userInfo?.id,
				carId,
			},
			reason: 'PAYMENT',
		};
		await postCancelsHomeService(customerLeadApplicationUuid, params);
	}, [carId, customerLeadApplicationUuid, userInfo?.id]);

	// encarpay, encarloan - 결제수단 수정
	const putPaymentMethodsFn = useCallback(
		async (paymentMethod = 'CASH', loanVersion) => {
			await putPaymentMethods(customerLeadApplicationUuid, {
				paymentMethod,
				...(loanVersion && { version: loanVersion }),
			});
		},
		[customerLeadApplicationUuid],
	);

	// encarpay - 한도 조회 결과 저장
	const postLoansEncarpayLimitFn = useCallback(
		async (encarPayId, amount) => {
			await postLoansEncarpayLimit(customerLeadApplicationUuid, {
				encarPayId,
				amount,
			});
		},
		[customerLeadApplicationUuid],
	);

	// encarloan - 한도 조회 결과 저장
	const postLoansEncarloanLimitFn = useCallback(
		async (encarPayId, amount) => {
			await postLoansEncarloanLimit(customerLeadApplicationUuid, {
				limitInquiryId: encarPayId,
				amount,
			});
		},
		[customerLeadApplicationUuid],
	);

	// encarpay - 한도조회 가능여부 체크 (이미 한도조회를 한 경우 재조회 못하도록)
	const checkAccessEncarpayLimitFn = useCallback(async () => {
		const {
			data: { loan },
		} = await getHomeServiceApplications(customerLeadApplicationUuid, userInfo?.id, carId);
		const { loanType, encarPayId } = loan;
		if (loanType === 'ENCAR_PAY' && encarPayId) {
			return true;
		}
		return false;
	}, [carId, customerLeadApplicationUuid, userInfo?.id]);

	// 차량리포트 - 페이지 진입 시, ‘차량 확인' 완료 상태로 간주
	const postReportConfirmFn = useCallback(
		async (paramCarId) => {
			if (customerLeadApplicationUuid && userInfo?.id) {
				await postVerificationReportConfirm(customerLeadApplicationUuid, {
					requesterType: 'USER',
					requesterId: userInfo?.id,
					carId: paramCarId,
				});
			}
		},
		[customerLeadApplicationUuid, userInfo?.id],
	);

	// 성능점검&보험이력 - 보험이력 탭까지 확인하지 않아도 성능점검 페이지 진입만 하면 완료
	const postInspectionAndInsuranceConfirmFn = useCallback(
		async (paramCarId) => {
			if (customerLeadApplicationUuid && userInfo?.id) {
				await postInspectionInsuranceConfirm(customerLeadApplicationUuid, {
					requesterType: 'USER',
					requesterId: userInfo?.id,
					carId: paramCarId,
				});
			}
		},
		[customerLeadApplicationUuid, userInfo?.id],
	);

	// 차량리포트, 엔카페이 한도조회 결과(에러) 채팅방 아이디 가져오기
	const getChatGroupIdFn = useCallback(
		async (paramCarId) => {
			let paramId = customerLeadApplicationId;
			if (!paramId) {
				const { data: applicationData } = await getHomeServiceApplications(customerLeadApplicationUuid, userInfo?.id, carId || paramCarId);
				paramId = applicationData.id;
			}
			const data = await getChatGroup(paramId);
			const existChatId = data?.data[0]?.chatId;
			return existChatId;
		},
		[carId, customerLeadApplicationId, customerLeadApplicationUuid, userInfo?.id],
	);

	// buy 절차페이지 이동
	const goWorkflowPage = useCallback(
		({ replace } = {}) => {
			const url = new URL('/home-service/workflow', buyHost);
			url.searchParams.set('customerLeadApplicationUuid', customerLeadApplicationUuid);
			if (replace) window.location.replace(url);
			else window.location.href = url;
		},
		[customerLeadApplicationUuid],
	);

	// 로그인 체크
	const checkLogin = useCallback(() => {
		const loginComplete = isLoggedIn && !!userInfo;

		if (!loginComplete) {
			const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
			goLogin({ returnUrl });
			return false;
		}
		return true;
	}, [goLogin, isLoggedIn, location.pathname, location.search, userInfo]);

	// 신청서, 로그인 정보 체크
	const checkIsSameApplicant = useCallback(async () => {
		try {
			const { data: workflowData } = await getHomeServiceUserProcess(customerLeadApplicationUuid);
			setHomeServiceInfo(workflowData);
			if (workflowData.applicantUserId !== userInfo?.id) {
				alert('신청자 정보와 맞지 않습니다. 정보를 다시 확인해주세요.');
				handleUniversalClose();
				return false;
			}
			return true;
		} catch (e) {
			loggingException(e);
			alert('일시적인 오류가 있습니다. 잠시후 다시 시도해주세요.');
			handleUniversalClose();
			return false;
		}
	}, [customerLeadApplicationUuid, handleUniversalClose, userInfo?.id]);

	// 결제 완료 금액 체크
	const checkcompletedPaymentAmount = useCallback(async (applicationUuid, orderTransactionId, validAmount) => {
		let completedPaymentAmount = 0;
		let partialCanceledWarrantyCost = 0;

		// A 입금한 금액세팅 (B는 포함되지않는 금액)
		await getPaymentEstimates(applicationUuid, { includeCompletedPayment: true }).then(({ data: estimate }) => {
			completedPaymentAmount = estimate.completedPaymentAmount ?? 0;
		});

		// B 부분취소된 보증비 금액세팅
		await getTransaction(orderTransactionId).then(({ data: orderTransaction }) => {
			const partialCanceledWarrantyAccount = orderTransaction.payments.find(({ status }) => status === ORDERS_PAYMENT_STATUS.PARTIAL_CANCELED);
			if (partialCanceledWarrantyAccount) {
				partialCanceledWarrantyCost = partialCanceledWarrantyAccount.amount - partialCanceledWarrantyAccount.actualAmount;
			}
		});

		// Toss KYC인증 대상은 부분취소한 금액까지 포함하여 토탈 금액이 1천만원 이상 입금한 고객인지를 체크함
		if (completedPaymentAmount + partialCanceledWarrantyCost >= validAmount) {
			return true;
		}

		return false;
	}, []);

	// h3에서 진입한 경우 로그인 정보 갱신
	useEffect(() => {
		if (customerLeadApplicationUuid && !skipAuth) {
			getUser({
				checkAuth: true,
				passAuth: true,
			});
		}
	}, [customerLeadApplicationUuid, getUser, skipAuth]);

	// h3에서 진입한 경우 비로그인 시 로그인 페이지로 이동
	useEffect(() => {
		(async () => {
			if (customerLeadApplicationUuid && !skipAuth) {
				if (!initAuth || checkAuth) return;
				const isLogin = checkLogin();
				if (isLogin) {
					const isCheckSameApplicant = await checkIsSameApplicant();
					if (isCheckSameApplicant) {
						setCheckAuth(true);
					}
					return;
				}
			}
			setCheckAuth(true);
		})();
	}, [checkAuth, checkIsSameApplicant, checkLogin, customerLeadApplicationUuid, initAuth, skipAuth]);

	useEffect(() => {
		if (customerLeadApplicationUuid && checkAuth && checkAccessEncarpayLimit) {
			(async () => {
				try {
					const alreadyLimitCheck = await checkAccessEncarpayLimitFn();
					if (alreadyLimitCheck) {
						setAccessEncarpayLimitType(TYPE_LIMIT.IMPOSSIBLE);
						return;
					}
					setAccessEncarpayLimitType(TYPE_LIMIT.POSSIBLE);
				} catch (e) {
					loggingException(e);
					alert('일시적인 오류가 있습니다. 잠시후 다시 시도해주세요.');
					goWorkflowPage();
				}
			})();
		}
	}, [checkAccessEncarpayLimit, checkAccessEncarpayLimitFn, checkAuth, customerLeadApplicationUuid, goWorkflowPage]);

	return {
		checkAuth,
		accessEncarpayLimitType,
		carId,
		customerLeadApplicationUuid,
		customerLeadApplicationId,
		homeServiceInfo,
		getApplication,
		getPaymentEstimateInfoFn,
		getEncarPayHashFn,
		getNomineesInfoFn,
		postEncarPayFinancialConsultationFn,
		postEncarLoanFinancialConsultationFn,
		postCancelsHomeServiceFn,
		putPaymentMethodsFn,
		postLoansEncarpayLimitFn,
		postLoansEncarloanLimitFn,
		postReportConfirmFn,
		postInspectionAndInsuranceConfirmFn,
		getChatGroupIdFn,
		goWorkflowPage,
		findWorkflowSubStepStatus,
		TYPE_LIMIT,
		checkcompletedPaymentAmount,
		checkIsSameApplicant,
	};
}
